import type { ActionTree, ActionContext, Module, MutationTree } from 'vuex';
import type { AxiosError, AxiosResponse } from 'axios';
import Cookies from 'cookies-js';
import moment from 'moment';

import {
	AlertNotificationConstant,
	CookieConstant,
	ExperienceTypeConstant,
	MemberStateConstant,
	MessageConstant,
	ProfileModuleConstant,
	StorageConstant
} from '../../constants';
import { memberStateService } from '../../services/memberStateService';
import { profileService as oldProfileService } from '../../services/profileService';
import {
	profileService,
	sessionService,
	storageService
} from '@life-time-inc/enterprise-framework-core';
import { featureFlagService } from '../../services/featureFlagService';
import QueryStringParser from '../../../lib/helpers/queryStringParser';
import QueryStringParamRemover from '../../../lib/helpers/queryStringParamRemover';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';

interface IProfileState {
	profile: ltFramework.IProfile;
}

const ProfileState: IProfileState = {
	profile: {
		memberState: MemberStateConstant.unknown,
		initialized: false,
		verified: false
	} as ltFramework.IProfile
};

const { action, mutation } = ProfileModuleConstant;

const ProfileMutations: MutationTree<IProfileState> = {
	[mutation.setProfile](state: IProfileState, profile: ltFramework.IProfile) {
		state.profile = profile;

		if (profile?.memberDetails?.membershipAttributes) {
			runMembershipChecks(profile);
		}
	}
};

interface IBatchJob {
	key: string;
	job: () => void;
}

const savedSearchBatchJobs: IBatchJob[] = [];

const ProfileActions: ActionTree<IProfileState, ltFramework.IRootState> = {
	[action.initialize]({
		commit,
		state
	}: ActionContext<IProfileState, ltFramework.IRootState>): Promise<void> {
		// initialize profileStore differently when limitedProfile is true
		if (window.lt.pageProperties?.limitedProfile) {
			const storedMemberDetails = storageService.sessionGet<ltFramework.IMemberDetails>(
				StorageConstant.memberDetails
			) as ltFramework.IMemberDetails;

			const limitedProfile = state.profile;

			if (storedMemberDetails) {
				limitedProfile.memberDetails = clearPeriodData(storedMemberDetails);
				limitedProfile.memberState = MemberStateConstant.remembered;
				limitedProfile.initialized = true;
				const aemToken = Cookies.get(CookieConstant.aemToken);

				if (aemToken) {
					sessionService.setCookie(
						CookieConstant.aemToken,
						aemToken,
						CookieConstant.aemTokenExpires
					);
				}

				if (limitedProfile.memberDetails) {
					checkMembershipReactivationStatus(limitedProfile);
				}

				commit(mutation.setProfile, limitedProfile);
			} else {
				limitedProfile.memberState = MemberStateConstant.unauthenticated;
				limitedProfile.initialized = true;

				commit(mutation.setProfile, limitedProfile);
			}
			return Promise.resolve();
		}
		let parameterPassed = undefined;
		let st: string = QueryStringParser('st');
		if (st) {
			parameterPassed = 'st';
			sessionService.invalidateSession();
			sessionService.setCookie(CookieConstant.ltAuthentication, st, CookieConstant.ssoIdExpires);
		} else {
			st = Cookies.get(CookieConstant.ltAuthentication);
		}

		// Check for existing SSO information prioritizing an SSO passed in as a URL parameter before the cookie
		let sso: string = QueryStringParser('sso');
		if (sso) {
			parameterPassed = 'sso';
			sessionService.setCookie(CookieConstant.ssoId, sso, CookieConstant.ssoIdExpires);
		} else {
			sso = Cookies.get(CookieConstant.ssoId);
		}

		let profileToken: string = Cookies.get(CookieConstant.profileToken);
		let rememberToken: string = Cookies.get(CookieConstant.ltAuthorization);
		if (rememberToken) {
			sessionService.invalidateProfileToken();
			profileToken = '';
		}
		let ct: string = profileService.getCustomerToken();
		// Check for a ct (Customer Token) passed in as a URL parameter and if so clear the SSO and Profile Token, but still allowing
		// for subsequent requests without the ct URL parameter to reset the store back to the original user due to the SSOID and
		// Profile Token being stored in the cookie. This is implemented per the business's request.
		if (ct) {
			sso = '';
			profileToken = '';
			rememberToken = '';
			parameterPassed = 'ct';
		}
		if (parameterPassed) {
			const urlStore: ltFramework.IUrlStore = window.ef.urlStore as ltFramework.IUrlStore;
			const currentUrl = urlStore.getInitialData().currentUrl;
			const queryParamString = currentUrl.search;
			const newQueryParamString = QueryStringParamRemover(queryParamString, parameterPassed);
			const currentPathName = currentUrl.pathname;
			const urlHash = currentUrl.hash;
			const newPathName = currentPathName.endsWith('/lta')
				? currentPathName.substring(0, currentPathName.lastIndexOf('/lta'))
				: currentPathName;
			const newHref = newPathName + newQueryParamString + urlHash;

			history.replaceState(null, '', newHref);
		}

		if (!st && !sso && !profileToken && !ct && !rememberToken) {
			return new Promise<void>((resolve) => {
				const errorProfile: ltFramework.IProfile = {} as ltFramework.IProfile;
				errorProfile.sso = sso;
				errorProfile.token = profileToken;
				errorProfile.authToken = ct;
				errorProfile.partyId = null;
				errorProfile.personalization = null;
				errorProfile.memberDetails = null;
				errorProfile.memberState = MemberStateConstant.unauthenticated;
				errorProfile.initialized = true;
				errorProfile.verified = true;

				commit(mutation.setProfile, errorProfile);

				resolve();
			});
		}

		// Avoid expected 401 error if authentication happened over an hour ago
		const ltSsoCreatedCookie = Number(Cookies.get(CookieConstant.ltSsoCreated));
		if (ltSsoCreatedCookie) {
			const millisecondsPerSecond = 1000;
			const secondsPerMinute = 60;
			const minutesPerHour = 60;
			const oneHour = millisecondsPerSecond * secondsPerMinute * minutesPerHour;
			const timeSinceAuthentication = Date.now() - ltSsoCreatedCookie;

			if (timeSinceAuthentication > oneHour) {
				return new Promise<void>((resolve) => {
					sso = '';
					ct = '';
					sessionService.invalidateAuthSession();
					const memberState = memberStateService.getMemberState(
						st,
						sso,
						rememberToken || profileToken,
						ct
					);
					// The party Id, member details, and personalization will be removed from the session storage in the above call if the user is unauthenticated
					const partyId = storageService.sessionGet<number>(StorageConstant.partyId) as number;
					const personalization = storageService.sessionGet<ltFramework.IPersonalization>(
						StorageConstant.personalization
					) as ltFramework.IPersonalization;
					const memberDetails = storageService.sessionGet<ltFramework.IMemberDetails>(
						StorageConstant.memberDetails
					) as ltFramework.IMemberDetails;

					const errorProfile: ltFramework.IProfile = {} as ltFramework.IProfile;
					errorProfile.sso = sso;
					errorProfile.token = profileToken;
					errorProfile.jwt = rememberToken;
					errorProfile.authToken = ct;
					errorProfile.partyId = partyId;
					errorProfile.personalization = personalization;
					errorProfile.memberDetails = memberDetails;
					errorProfile.memberState = memberState;
					errorProfile.initialized = true;
					errorProfile.verified = true;

					commit(mutation.setProfile, errorProfile);

					resolve();
				});
			}
		}

		const aemToken = Cookies.get(CookieConstant.aemToken);
		let memberState = memberStateService.getMemberState(
			st,
			sso,
			rememberToken ? rememberToken : profileToken,
			ct
		);

		let partyId: null | number = null;
		let personalization = {} as ltFramework.IPersonalization;
		let memberDetails = {} as ltFramework.IMemberDetails;

		// If the ct (Customer Token) is passed in as a URL parameter do not get the details
		// from the session since it would be from the current user logged in.
		if (!ct) {
			partyId = storageService.sessionGet<number>(StorageConstant.partyId) as number;
			personalization = storageService.sessionGet<ltFramework.IPersonalization>(
				StorageConstant.personalization
			) as ltFramework.IPersonalization;
			memberDetails = storageService.sessionGet<ltFramework.IMemberDetails>(
				StorageConstant.memberDetails
			) as ltFramework.IMemberDetails;
			if (memberDetails) {
				memberDetails = profileService.checkGuestPassBalance(memberDetails);
			}
		}

		const storageProfile: ltFramework.IProfile = {} as ltFramework.IProfile;
		storageProfile.sso = sso;
		storageProfile.token = profileToken;
		storageProfile.jwt = rememberToken;
		storageProfile.authToken = ct;
		storageProfile.partyId = partyId;
		storageProfile.personalization = personalization;
		storageProfile.memberDetails = memberDetails;
		storageProfile.memberState = memberState;
		storageProfile.initialized =
			memberDetails && (memberDetails.isEmployee || memberDetails.memberId > 0);
		storageProfile.verified = false;

		// if there's a jwt, also set old remember me token
		// needed for SchedReg components
		if (storageProfile.jwt) {
			storageProfile.token = storageProfile.jwt;
		}
		commit(mutation.setProfile, storageProfile);

		return oldProfileService.getProfile().then(
			(response: AxiosResponse<ltFramework.IProfileResponse>) => {
				const setDomainCookies = featureFlagService.isEnabled('domaincookies');
				memberState = memberStateService.getMemberState(
					ct,
					sso,
					response.data.jwt ? response.data.jwt : response.data.token,
					ct
				);

				const responseProfile: ltFramework.IProfile = {} as ltFramework.IProfile;
				responseProfile.sso = sso;
				responseProfile.jwt = response.data.jwt;
				responseProfile.token = response.data.token;
				responseProfile.authToken = ct;
				responseProfile.partyId = response.data.partyId;
				responseProfile.personalization = response.data.personalization;
				responseProfile.memberDetails = response.data.memberDetails;
				responseProfile.memberState = memberState;
				responseProfile.initialized = true;
				responseProfile.verified = true;

				// If the ct (Customer Token) is passed in as a URL parameter do not set the session
				// so we can return back to the original user on the subsequent request.
				if (!ct) {
					storageService.sessionSet(StorageConstant.partyId, responseProfile.partyId);
					storageService.sessionSet(StorageConstant.memberDetails, responseProfile.memberDetails);
					storageService.sessionSet(
						StorageConstant.personalization,
						responseProfile.personalization
					);
				}

				// If the ct (Customer Token) is passed in as a URL parameter do not set the the cookie
				// so we can return back to the original user on the subsequent request.
				if (responseProfile.sso && !ct) {
					sessionService.setCookie(
						CookieConstant.ssoId,
						responseProfile.sso,
						CookieConstant.ssoIdExpires
					);
				}

				// If the ct (Customer Token) is passed in as a URL parameter do not set the the cookie
				// so we can return back to the original user on the subsequent request.
				if (responseProfile.token && !ct) {
					sessionService.setCookie(
						CookieConstant.profileToken,
						responseProfile.token,
						CookieConstant.profileTokenExpires
					);
				}

				if (aemToken) {
					sessionService.setCookie(
						CookieConstant.aemToken,
						aemToken,
						CookieConstant.aemTokenExpires
					);
				}
				// if there's a jwt, also set old remember me token
				// needed for SchedReg components
				if (responseProfile.jwt) {
					responseProfile.token = responseProfile.jwt;
					sessionService.setCookie(
						CookieConstant.ltAuthorization,
						responseProfile.jwt,
						CookieConstant.profileTokenExpires,
						setDomainCookies
					);
				}
				commit(mutation.setProfile, responseProfile);
			},
			(response: AxiosError | Error) => {
				const axiosError: AxiosError = response as AxiosError;
				if (axiosError && axiosError.response) {
					switch (axiosError.response.status) {
						case 400:
						case 401:
						case 502:
							// (WEB-19740 Remember me cookie not deleted when the token expires causing the profile and Schedules to fail)
							// adding this if statement block to cover case when Profile API call fails (401 response status) because of
							// expired 'ltf-profile' token. In this case we need to invalidate it to avoid failure of other calls/services,
							// which might use it for authentication.
							if (memberState === MemberStateConstant.remembered) {
								profileToken = '';
								rememberToken = '';
								sessionService.invalidateProfileToken();
								sessionService.invalidateRememberToken();
							}
							sso = '';
							if (ct) {
								// clear this because we want the failure of ct
								// to override possible authToken (from cookie)
								// AEMPLAT-542
								st = '';
							}
							ct = '';
							sessionService.invalidateAuthSession();
							break;
						default:
							break;
					}
				}

				const error: Error = response as Error;
				if (error && error.message) {
					switch (error.message) {
						case MessageConstant.expiredSsoMessage:
						case MessageConstant.timeout:
							sso = '';
							ct = '';
							sessionService.invalidateAuthSession();
							break;
						case MessageConstant.invalidRequestMessage:
						case MessageConstant.profileNotFoundMessage:
							sso = '';
							ct = '';
							profileToken = '';
							rememberToken = '';
							sessionService.invalidateSession();
							break;
						default:
							break;
					}
				}

				memberState = memberStateService.getMemberState(
					st,
					sso,
					rememberToken ? rememberToken : profileToken,
					ct
				);
				// The party Id, member details, and personalization will be removed from the session storage in the above call if the user is unauthenticated
				partyId = storageService.sessionGet<number>(StorageConstant.partyId) as number;
				personalization = storageService.sessionGet<ltFramework.IPersonalization>(
					StorageConstant.personalization
				) as ltFramework.IPersonalization;
				memberDetails = storageService.sessionGet<ltFramework.IMemberDetails>(
					StorageConstant.memberDetails
				) as ltFramework.IMemberDetails;

				const errorProfile: ltFramework.IProfile = {} as ltFramework.IProfile;
				errorProfile.sso = sso;
				errorProfile.token = profileToken;
				errorProfile.jwt = rememberToken;
				errorProfile.authToken = ct;
				errorProfile.partyId = partyId;
				errorProfile.personalization = personalization;
				errorProfile.memberDetails = memberDetails;
				errorProfile.memberState = memberState;
				errorProfile.initialized = true;
				errorProfile.verified = true;

				commit(mutation.setProfile, errorProfile);
			}
		);
	},

	/** ONLY called by legacy `lt-aem` logout */
	[action.logout](): Promise<AxiosResponse<ltFramework.IProfileLogoutResponse> | void> {
		window.ef.alertNotificationStore.open({
			id: 'logout-success',
			type: AlertNotificationConstant.alertNotificationSuccess,
			message: 'You have been logged out successfully.',
			defer: true,
			dismissible: true,
			title: ''
		});

		let targetPath = '/';
		if (
			window.lt.pageProperties?.cugEnabled &&
			window.lt.pageProperties?.experienceType === ExperienceTypeConstant.member
		) {
			const clubObj = window.ef?.clubStore?.getInitialData?.()?.club;
			if (clubObj?.link) {
				targetPath = clubObj.link;
			}
		}

		sessionService.invalidateSession();

		Cookies.expire(CookieConstant.ltAuthentication);
		Cookies.expire('lt-auth');
		Cookies.expire('lt-member-spabiz-token');

		const undefinedZone = undefined;

		storageService.sessionRemoveKeys(
			undefinedZone,
			'lt-dismissed-limited-access-notification',
			'lt-onhold-reactivation-alert-eligible',
			'lt-reactivation-pending',
			'lt-student-reactivation-alert-eligible'
		);

		storageService.localSet('userSignedOut', true);

		if (targetPath) {
			window.location.href = targetPath;
		} else {
			window.location.reload();
		}

		return Promise.resolve();
	},

	[action.updateSchregSavedSearches](
		{ commit, state }: ActionContext<IProfileState, ltFramework.IRootState>,
		payload: {
			savedSearches: ltFramework.ISchregSavedSearch[];
			successCallback: (response: AxiosResponse<ltFramework.IUpdateProfileMetaResponse>) => void;
			failCallback: (response: Record<string, unknown>) => void;
		}
	): Promise<void> {
		const { savedSearches, successCallback, failCallback } = payload;

		let savedSearchKey = '';
		for (const savedSearch of savedSearches) {
			savedSearchKey += String(savedSearch.id);
		}
		const hasToken = Boolean(state.profile.token);
		//create a batch job to update saved search data in case we don't have
		//access to the remote profile yet
		const batch = createBatchJob(
			hasToken,
			savedSearchKey,
			savedSearches,
			(responseData) => {
				const newProfile = {
					...state.profile
				};
				if (!newProfile.personalization) {
					newProfile.personalization = {} as ltFramework.IPersonalization;
				}
				newProfile.personalization.schregSavedSearches = savedSearches;

				commit(mutation.setProfile, newProfile);
				if (typeof successCallback === 'function') {
					successCallback(responseData as AxiosResponse<ltFramework.IUpdateProfileMetaResponse>);
				}
			},
			(failedResponse) => {
				if (typeof failCallback === 'function') {
					failCallback(failedResponse);
				}
			}
		);

		//add job to the batch
		if (batch != null) {
			savedSearchBatchJobs.push(batch);
		}

		//if we already have the remote profile, process it now
		if (state.profile.initialized) {
			processBatchJobs(savedSearchBatchJobs);
			savedSearchBatchJobs.length = 0;
		}

		return Promise.resolve();
	}
};

export const ProfileModule: Module<IProfileState, ltFramework.IRootState> = {
	namespaced: true,
	state: ProfileState,
	actions: ProfileActions,
	mutations: ProfileMutations
};

/////////////////////////////////////////
// Update Schreg Saved Searches Functions
/////////////////////////////////////////

function createBatchJob(
	hasToken: boolean,
	savedSearchKey: string,
	savedSearches: ltFramework.ISchregSavedSearch[],
	successCallback: (response: AxiosResponse<ltFramework.IUpdateProfileMetaResponse>) => void,
	failCallback: (response: { statusCode: string; statusText: string }) => void
) {
	//creates a key so as to not create duplicate jobs
	const batchKey = savedSearchKey;
	const alreadyExists = savedSearchBatchJobs.some((job) => {
		return job.key === batchKey;
	});

	function batchJob() {
		profileService.updateSchregSavedSearches(savedSearches).then(
			//Success - update data and trigger change
			(responseData) => {
				if (typeof successCallback === 'function') {
					successCallback(responseData as AxiosResponse<ltFramework.IUpdateProfileMetaResponse>);
				}
			},
			//Error - log error
			(failedResponse: { statusCode: string; statusText: string }) => {
				//notify of message
				console.error(
					`Profile call returned an error: ${failedResponse.statusCode} ${
						failedResponse.statusText
					} - when updating schregSavedSearches with ${JSON.stringify(savedSearches)}`
				);
				if (typeof failCallback === 'function') {
					failCallback(failedResponse);
				}
			}
		);
	}

	if (!alreadyExists && hasToken) {
		return {
			key: batchKey,
			job: batchJob
		};
	} else {
		return null;
	}
}

function processBatchJobs(jobs: IBatchJob[]) {
	while (jobs.length) {
		const batchJob = jobs.shift() || { job: null };

		if (typeof batchJob.job === 'function') {
			batchJob.job();
		}
	}
}

//////////////////////////////////
// Run Membership Check Functions
//////////////////////////////////

function runMembershipChecks(profile: ltFramework.IProfile) {
	checkMembershipReactivationStatus(profile);
	const features = window.lt && window.lt.features ? window.lt.features : [];
	// check if a user purchased a one day membership
	checkOneDayMembershipUpgradeEligibility(features, profile);
}

const membershipUpgradeBannerId = 'membershipupgradebanner';

function checkOneDayMembershipUpgradeEligibility(
	features: ltFramework.IFeatures[],
	data: ltFramework.IProfile
) {
	const dayPassMtaId = 51;
	const { memberState: profileMemberState } = data;
	const membershipUpgradeBannerFlag = features.some(
		(e) => e.featureId === membershipUpgradeBannerId && e.enabled
	);
	const attrs = data.memberDetails?.membershipTypeAttributes || [];
	const hasDayPassMta = attrs.some((mta) => mta.key === dayPassMtaId);
	if (
		membershipUpgradeBannerFlag &&
		hasDayPassMta &&
		(profileMemberState === MemberStateConstant.authenticated ||
			profileMemberState === MemberStateConstant.remembered)
	) {
		if (window.lt.pageProperties?.notificationsInfo?.membershipNotification) {
			const clubsData = window.ef.clubsStore.getInitialData();
			if (clubsData.initialized && clubsData.clubs && clubsData.clubs.length > 0) {
				triggerMembershipNotificationMessage();
			} else {
				window.ef.clubsStore.listen(triggerMembershipNotificationMessage);
			}
		}
	}
}

function triggerMembershipNotificationMessage() {
	const notificationExists = window.ef.alertNotificationStore
		.getInitialData()
		.some(({ id }) => membershipUpgradeBannerId === id);
	if (notificationExists) {
		return;
	}
	const message =
		window.lt.pageProperties?.notificationsInfo?.membershipNotification
			?.oneDayMembershipBannerHtml || '';
	const infoAlert: ltFramework.IAlertNotification = {
		id: membershipUpgradeBannerId,
		message,
		type: AlertNotificationConstant.alertNotificationInfo,
		title: ''
	};
	window.ef.alertNotificationStore.open(infoAlert);
}

// check for student flex/on-hold status and store in session storage
function checkMembershipReactivationStatus(profile: ltFramework.IProfile) {
	let studentActivatedSameMonth;
	let onHoldActivatedSameMonth;
	let onHoldTerminationDateSameMonth;
	const constants = {
		onHoldReactivationAlertEligible: 'lt-onhold-reactivation-alert-eligible',
		studentReactivationAlertEligible: 'lt-student-reactivation-alert-eligible'
	};
	// gather array of student flex membership attributes that are currently valid
	const studentEligibleAttributes = profile.memberDetails?.membershipAttributes.filter((ma) => {
		return (
			ma.name === 'Eligible for Online Student Reactivation' &&
			moment(ma.effectiveThruDate).isAfter(moment.utc())
		);
	});
	// check if they have an activation date set in the current month
	if (studentEligibleAttributes?.length) {
		studentActivatedSameMonth = checkReactivationStatus(studentEligibleAttributes[0]);
	}
	// check for student flex reactivation notification eligibility property on profile
	const studentEligible =
		studentEligibleAttributes?.length &&
		profile.memberDetails?.membershipTypeAttributes.some((mta) => {
			return mta.value === 'Student Flex';
		}) &&
		profile.memberDetails.membershipStatus === 'Active';
	// if eligible, store flag to display student flex reactivation notification in AlertNotificationStore
	if (studentEligible && !studentActivatedSameMonth) {
		storageService.sessionSet(constants.studentReactivationAlertEligible, true);
	}
	// gather array of on-hold membership attributes that are currently valid
	const onHoldEligibleAttributes = profile.memberDetails?.membershipAttributes.filter((ma) => {
		return (
			ma.name === 'Eligible for Online On-Hold Reactivation' &&
			moment(ma.effectiveThruDate).isAfter(moment.utc())
		);
	});
	// check if resourceKey is not expired
	const validDigitalResourceKey = profile.memberDetails?.resourceKeys
		? profile.memberDetails?.resourceKeys.some((rk) => {
				return (
					rk.name === 'LT Digital Conversion' &&
					moment(rk.thruDate, 'YYYY-MM-DDTHH:mm:ssZ').isAfter(moment())
				);
			})
		: false;
	// check if they have an activation date set in the current month
	if (onHoldEligibleAttributes?.length) {
		onHoldActivatedSameMonth = checkReactivationStatus(onHoldEligibleAttributes[0]);
		onHoldTerminationDateSameMonth = moment(
			profile.memberDetails?.terminationDate,
			'YYYY-MM-DDTHH:mm:ssZ'
		).isSame(moment(), 'month');
	}
	// check for digital reactivation notification eligibility property on profile
	const onHoldEligible =
		onHoldEligibleAttributes?.length &&
		profile.memberDetails?.membershipTypeAttributes.some((mta) => {
			return mta.value === 'LT Digital Conversion';
		}) &&
		(profile.memberDetails.membershipStatus === 'Active' ||
			profile.memberDetails.membershipStatus === 'Pending Termination');

	// store on hold reactivation notification alert
	if (
		validDigitalResourceKey &&
		onHoldEligible &&
		!onHoldActivatedSameMonth &&
		!onHoldTerminationDateSameMonth
	) {
		// if eligible, grab first element from attribute array
		const memberAttribute = onHoldEligibleAttributes[0];
		// construct date string if applicable
		let dateString = '';
		if (memberAttribute.value) {
			dateString = moment(memberAttribute.value).format('MMMM D, YYYY');
		}
		// store flag to display on-hold notification alert in AlertNotificationStore
		storageService.sessionSet(constants.onHoldReactivationAlertEligible, {
			eligible: true,
			date: dateString
		});
	}
}

function checkReactivationStatus(attribute: ltFramework.IMembershipAttribute) {
	if (attribute.value) {
		return moment(attribute.value).isSame(moment(), 'month');
	} else {
		return false;
	}
}

/////////////////////////////
// Limited Profile Functions
/////////////////////////////
const earlyDate = moment('0001-01-01T00:00:00.000Z', moment.ISO_8601);

function clearPeriodData(memberDetails: ltFramework.IMemberDetails): ltFramework.IMemberDetails {
	if (memberDetails) {
		const valLastCacheDate = moment(memberDetails.lastCacheDate, moment.ISO_8601);

		// if the last cache date is not the current month set the visits to 0 and max out the guest passes
		if (
			valLastCacheDate.isBefore(moment().startOf('month')) &&
			valLastCacheDate.isAfter(earlyDate)
		) {
			memberDetails.clubVisits = 0;
			// reset the value for a monthly guest pass person
			if (memberDetails.guestPassPeriod && memberDetails.guestPassPeriod === 'Month') {
				memberDetails.guestPassBalance = memberDetails.guestPassTotal;
			}
		}
		// reset the value for a yearly guest pass person
		if (
			memberDetails.guestPassPeriod &&
			memberDetails.guestPassPeriod === 'Year' &&
			valLastCacheDate.isBefore(moment().startOf('year')) &&
			valLastCacheDate.isAfter(earlyDate)
		) {
			memberDetails.guestPassBalance = memberDetails.guestPassTotal;
		}
	}
	return memberDetails;
}
